/**
 * Loop component for Steve Render Service.
 *
 * Repeats its children by placing them inside a Sequence
 * at the appropriate looped offset.
 *
 * Written from scratch for Steve.AI.
 */

import React, { useMemo } from 'react';
import { Sequence } from '../Sequence.js';
import { useCurrentFrame } from '../use-current-frame.js';
import { useVideoConfig } from '../use-video-config.js';
import { validateDurationInFrames } from '../validation/validate-duration-in-frames.js';

export const Loop = ({
  durationInFrames,
  times = Infinity,
  children,
  name,
  ...props
}) => {
  const currentFrame = useCurrentFrame();
  const { durationInFrames: compDuration } = useVideoConfig();

  validateDurationInFrames({
    durationInFrames,
    component: 'of the <Loop /> component',
    allowFloats: true,
  });

  if (typeof times !== 'number') {
    throw new TypeError(
      `Loop "times" must be a number, got ${typeof times}`
    );
  }

  if (times !== Infinity && times % 1 !== 0) {
    throw new TypeError(`Loop "times" must be an integer, got ${times}`);
  }

  if (times < 0) {
    throw new TypeError(`Loop "times" must be >= 0, got ${times}`);
  }

  const maxTimes = Math.ceil(compDuration / durationInFrames);
  const actualTimes = Math.min(maxTimes, times);
  const style = props.layout === 'none' ? undefined : props.style;
  const maxFrame = durationInFrames * (actualTimes - 1);
  const start = Math.floor(currentFrame / durationInFrames) * durationInFrames;
  const from = Math.min(start, maxFrame);

  const loopDisplay = useMemo(() => ({
    numberOfTimes: actualTimes,
    startOffset: -from,
    durationInFrames,
  }), [actualTimes, durationInFrames, from]);

  return (
    <Sequence
      durationInFrames={durationInFrames}
      from={from}
      name={name}
      loopDisplay={loopDisplay}
      layout={props.layout}
      style={style}
    >
      {children}
    </Sequence>
  );
};
